import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Download, Plus, Edit, Copy, Mail, FileText, ExternalLink, Check, Fuel, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { computeLoadGross } from '@/lib/invoiceHelpers';

const STATUS_TO_STEP = {
  'Available': 1,
  'Booked': 2,
  'In Transit': 3,
  'Delivered': 4,
  'Invoiced': 6,
  'Claim': 4,
};

const STEPS = [
  { num: 1, label: 'Planned' },
  { num: 2, label: 'Dispatched' },
  { num: 3, label: 'In Transit' },
  { num: 4, label: 'Delivered' },
  { num: 5, label: 'Delivered w/BOL' },
  { num: 6, label: 'Invoiced' },
  { num: 7, label: 'Paid' },
];

export default function LoadDetailModal({ load, onClose, onRefresh }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [iftaData, setIftaData] = useState([]);
  const [contacts, setContacts] = useState({});
  const [attachingFile, setAttachingFile] = useState(false);
  const [fuelAdvances, setFuelAdvances] = useState([]);
  const [receipts, setReceipts] = useState([]);

  useEffect(() => {
    if (load?.id) {
      loadNotes();
      loadSettlements();
      loadIFTA();
      loadContacts();
      loadFuelData();
    }
  }, [load?.id]);

  // Dispatcher-facing view of whether a driver has squared up a fuel
  // advance with receipts -- previously there was no visibility into this
  // at all on the dispatch side, only on the driver's own Active Load page.
  const loadFuelData = async () => {
    const [advances, expenses] = await Promise.all([
      client.entities.FuelAdvance.filter({ load_id: load.id }),
      client.entities.Expense.filter({ load_id: load.id }, '-date', 100),
    ]);
    setFuelAdvances(advances);
    setReceipts(expenses);
  };

  const loadNotes = async () => {
    const allMessages = await client.entities.LoadMessage.filter({ load_id: load.id }, '-created_date', 100);
    setNotes(allMessages);
  };

  const loadSettlements = async () => {
    const all = await client.entities.Settlement.filter({ load_id: load.id }, '-created_date', 10);
    setSettlements(all);
  };

  const loadIFTA = async () => {
    const all = await client.entities.IFTAMileageLog.filter({ load_id: load.id }, '-created_date', 1);
    if (all.length > 0) {
      try {
        const stateMileage = JSON.parse(all[0].state_mileage || '[]');
        setIftaData(stateMileage);
      } catch {
        setIftaData([]);
      }
    }
  };

  const loadContacts = async () => {
    const allContacts = await client.entities.Contact.list('-created_date', 100);
    const map = {};
    for (const c of allContacts) {
      if (!map[c.type]) map[c.type] = [];
      map[c.type].push(c);
    }
    setContacts(map);
  };

  const getCurrentStep = () => {
    let step = STATUS_TO_STEP[load.status] || 1;
    if (load.status === 'Delivered' && load.ebol_url) step = 5;
    return step;
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    await client.entities.LoadMessage.create({
      load_id: load.id,
      load_number: load.load_number,
      sender_email: 'admin',
      sender_name: 'Admin',
      sender_role: 'admin',
      message: newNote,
    });
    toast.success('Note added.');
    setNewNote('');
    loadNotes();
    setAddingNote(false);
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachingFile(true);
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    await client.entities.Load.update(load.id, { additional_documents: file_url });
    toast.success('File attached.');
    setAttachingFile(false);
    onRefresh?.();
  };

  const currentStep = getCurrentStep();
  const totalCharge = computeLoadGross(load);
  const rpm = load.miles ? (totalCharge / load.miles).toFixed(2) : 0;

  const settlement = settlements[0];

  return (
    <div className="fixed inset-0 z-50 flex bg-background overflow-hidden">
      {/* Panel */}
      <div className="flex-1 flex flex-col max-w-7xl mx-auto">
        {/* Header action bar */}
        <div className="flex-shrink-0 border-b border-border bg-white/3 px-6 py-4 flex items-center justify-between">
          <h2 className="font-space font-bold text-foreground">Load #{load.load_number}</h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs h-8" onClick={onClose}>
              ← Back to Loads
            </Button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Progress bar */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between gap-2 mb-2">
              {STEPS.map((step, i) => (
                <div key={step.num} className="flex-1">
                  <div className={cn(
                    "rounded-full h-10 flex items-center justify-center font-bold text-sm transition-all",
                    step.num === currentStep ? 'bg-primary text-primary-foreground' :
                    step.num < currentStep ? 'bg-green-500/30 text-green-400' :
                    'bg-muted/60 text-muted-foreground'
                  )}>
                    {step.num}
                  </div>
                  <p className="text-xs text-center text-muted-foreground mt-1 truncate">{step.label}</p>
                  {i < STEPS.length - 1 && (
                    <div className={cn(
                      "h-0.5 absolute top-5 left-1/2 w-[calc(50%-20px)]",
                      step.num < currentStep ? 'bg-green-500/30' : 'bg-muted/60'
                    )} style={{ marginLeft: '10px' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-3 gap-6">
            {/* Left — Details card */}
            <div className="col-span-2 glass-card p-6 space-y-4">
              <h3 className="font-space font-semibold text-foreground">Load Details</h3>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Load #</p>
                  <p className="font-semibold text-foreground">{load.load_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Trip Type</p>
                  <p className="font-semibold text-foreground">Company</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Pickup Date</p>
                  <p className="font-semibold text-foreground">{load.pickup_date || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Delivery Date</p>
                  <p className="font-semibold text-foreground">{load.delivery_date || '—'}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">From</p>
                <p className="font-semibold text-foreground text-sm">{load.pickup_address || load.origin || '—'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">To</p>
                <p className="font-semibold text-foreground text-sm">{load.dropoff_address || load.destination || '—'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Miles</p>
                  <p className="font-semibold text-foreground">{load.miles || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Deadhead Miles</p>
                  <p className="font-semibold text-foreground">{load.deadhead_miles || 0}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Driver</p>
                <p className="font-semibold text-foreground">{load.assigned_driver_name || '— Unassigned'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Weight</p>
                <p className="font-semibold text-foreground">{load.weight || '—'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Commodity</p>
                <p className="font-semibold text-foreground">{load.commodity || '—'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">Equipment</p>
                <p className="font-semibold text-foreground">{load.equipment_required || '—'}</p>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">PO / Pickup #</p>
                <p className="font-semibold text-foreground">{load.po_pickup_number || '—'}</p>
              </div>

              {(load.ratecon_url || load.ebol_url) && (
                <div className="col-span-2 flex flex-wrap gap-4">
                  {load.ratecon_url && (
                    <a href={load.ratecon_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> View Rate Con
                    </a>
                  )}
                  {load.ebol_url && (
                    <a href={load.ebol_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> View BOL
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Right — Fees card */}
            <div className="glass-card p-6 space-y-3">
              <h3 className="font-space font-semibold text-foreground">Fees & Charges</h3>
              
              <div className="space-y-2 text-sm border-b border-border pb-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Line Haul</span>
                  <span className="font-semibold text-foreground">${(load.line_haul_rate || 0).toFixed(2)}</span>
                </div>
                {load.fuel_surcharge > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Fuel Surcharge</span>
                    <span className="text-foreground">${load.fuel_surcharge.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {(load.tarp_fee > 0 || load.stop_off_fee > 0 || load.lumper_fee > 0 || load.detention_fee > 0 || load.additional_fee > 0) && (
                <div className="space-y-2 text-sm border-b border-border pb-3">
                  <p className="text-xs font-semibold text-primary uppercase">Accessory Fees</p>
                  {load.tarp_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tarp</span>
                      <span>${load.tarp_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {load.stop_off_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stop Off</span>
                      <span>${load.stop_off_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {load.lumper_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lumper</span>
                      <span>${load.lumper_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {load.detention_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Detention</span>
                      <span>${load.detention_fee.toFixed(2)}</span>
                    </div>
                  )}
                  {load.additional_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Additional</span>
                      <span>${load.additional_fee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-foreground font-semibold">Total Charge</span>
                  <span className="text-primary font-bold">${totalCharge.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Revenue / Mile</span>
                  <span className="text-primary">${rpm}/mi</span>
                </div>
              </div>
            </div>
          </div>

          {/* Settlement Details */}
          {settlement && (
            <div className="glass-card p-6">
              <h3 className="font-space font-semibold text-foreground mb-4">Settlement</h3>
              <div className="grid grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs mb-2">Driver: <span className="text-foreground font-semibold">{settlement.driver_name}</span></p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Gross Revenue</span>
                      <span className="text-foreground">${(settlement.gross_revenue || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Net to Driver</span>
                      <span className="text-green-400 font-semibold">${(settlement.final_net_pay ?? settlement.net_driver_pay ?? 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-2">Profit / Loss</p>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="text-foreground">${(settlement.gross_revenue || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Profit</span>
                      <span className={cn(
                        "font-semibold",
                        ((settlement.gross_revenue ?? 0) - (settlement.carrier_take_amount ?? 0)) >= 0 ? 'text-green-400' : 'text-red-400'
                      )}>
                        ${((settlement.gross_revenue ?? 0) - (settlement.carrier_take_amount ?? 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fuel Advance & Receipts -- dispatcher visibility into whether
              the driver has documented where an advance went, before
              settlement even exists yet */}
          {fuelAdvances.length > 0 && (() => {
            const approvedAdvance = fuelAdvances.filter(a => a.status === 'Approved').reduce((s, a) => s + (a.amount || 0), 0);
            const pendingAdvance = fuelAdvances.filter(a => a.status === 'Pending').reduce((s, a) => s + (a.amount || 0), 0);
            const documented = receipts.reduce((s, e) => s + (e.amount || 0), 0);
            const outstanding = Math.max(0, approvedAdvance - documented);
            return (
              <div className="glass-card p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Fuel className="w-4 h-4 text-amber-400" />
                  <h3 className="font-space font-semibold text-foreground">Fuel Advance & Receipts</h3>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Advance{pendingAdvance > 0 ? ' (approved)' : ''}</p>
                    <p className="font-semibold text-foreground">${approvedAdvance.toFixed(2)}</p>
                    {pendingAdvance > 0 && <p className="text-xs text-amber-400/70 mt-0.5">+${pendingAdvance.toFixed(2)} pending</p>}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Documented</p>
                    <p className="font-semibold text-green-400">${documented.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Outstanding</p>
                    <p className={cn('font-semibold', outstanding > 0 ? 'text-red-400' : 'text-green-400')}>
                      {outstanding > 0 ? `$${outstanding.toFixed(2)}` : 'Fully documented'}
                    </p>
                  </div>
                </div>
                {receipts.length > 0 ? (
                  <div className="divide-y divide-border border-t border-border">
                    {receipts.map(exp => (
                      <div key={exp.id} className="flex items-center justify-between py-2 text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-semibold px-2 py-0.5 rounded-full border text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30 flex-shrink-0">
                            {exp.category}
                          </span>
                          <span className="text-muted-foreground truncate">{exp.vendor || exp.date}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="font-semibold text-foreground">${(exp.amount || 0).toFixed(2)}</span>
                          {exp.receipt_url && (
                            <a href={exp.receipt_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border pt-3">
                    <Receipt className="w-3.5 h-3.5" /> No receipts uploaded yet.
                  </div>
                )}
              </div>
            );
          })()}

          {/* IFTA Miles by State */}
          {(iftaData.length > 0 || true) && (
            <div className="glass-card p-6">
              <h3 className="font-space font-semibold text-foreground mb-4">Miles by State</h3>
              {iftaData.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-3 py-2 text-muted-foreground font-medium">State</th>
                      <th className="text-right px-3 py-2 text-muted-foreground font-medium">Miles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iftaData.map((row, i) => (
                      <tr key={i} className="border-b border-border">
                        <td className="px-3 py-2 text-foreground">{row.state}</td>
                        <td className="text-right px-3 py-2 text-foreground">{row.miles}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-xs text-muted-foreground">No mileage data — will auto-populate on delivery.</p>
              )}
            </div>
          )}

          {/* Notes */}
          <div className="glass-card p-6">
            <h3 className="font-space font-semibold text-foreground mb-4">Notes</h3>
            <div className="space-y-3 mb-4">
              {notes.map(note => (
                <div key={note.id} className="border-l-2 border-primary/30 pl-3 py-1">
                  <p className="text-xs text-muted-foreground">{note.sender_name} — {new Date(note.created_date).toLocaleString()}</p>
                  <p className="text-sm text-foreground">{note.message}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Add a note…"
                className="flex-1 px-3 py-2 rounded-lg bg-transparent border border-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
              <Button size="sm" onClick={handleAddNote} disabled={addingNote || !newNote.trim()} className="bg-primary text-primary-foreground">
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}